import React from 'react';
import type { RenderManifestField } from '../../../types/kryspaceRenderManifest';
import styles from '../ControlBridge.module.css';

function formatEntityDetailFieldValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function isStructuredFieldValue(value: unknown): boolean {
  return value != null && typeof value === 'object';
}

export type EntityDetailVariant = 'artifact' | 'item';

export interface EntityDetailShellProps {
  variant: EntityDetailVariant;
  title: string;
  subtitle?: string;
  stateLabel?: string;
  fields?: RenderManifestField[];
  invokingLabel?: string;
  onClose: () => void;
  onBack?: () => void;
  footerPrimary?: React.ReactNode;
  children?: React.ReactNode;
}

function visibleFields(fields: RenderManifestField[] | undefined): RenderManifestField[] {
  return (fields ?? []).filter((f) => f.affordance !== 'hidden');
}

function FieldRows({ fields }: { fields: RenderManifestField[] }) {
  if (fields.length === 0) return null;
  return (
    <dl className={styles.entityDetailFieldList}>
      {fields.map((field) => (
        <div key={field.key} className={styles.entityDetailFieldRow}>
          <dt>{field.label}</dt>
          <dd
            className={
              isStructuredFieldValue(field.value) ? styles.entityDetailFieldJson : undefined
            }
          >
            {formatEntityDetailFieldValue(field.value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export const EntityDetailShell: React.FC<EntityDetailShellProps> = ({
  variant,
  title,
  subtitle,
  stateLabel,
  fields,
  invokingLabel,
  onClose,
  onBack,
  footerPrimary,
  children,
}) => {
  const core = visibleFields(fields).filter((f) => f.tier === 'core' || !f.tier);
  const context = visibleFields(fields).filter((f) => f.tier === 'context');
  const mechanical = visibleFields(fields).filter((f) => f.tier === 'mechanical');

  return (
    <aside
      className={styles.entityDetailShell}
      data-testid="entity-detail-shell"
      data-variant={variant}
      role="dialog"
      aria-label={variant === 'artifact' ? 'Artifact detail' : 'Artifact item detail'}
    >
      <header className={styles.entityDetailHeader}>
        <div className={styles.entityDetailHeaderRow}>
          {onBack ? (
            <button type="button" className={styles.entityDetailBack} onClick={onBack}>
              ← Back
            </button>
          ) : null}
          <button type="button" className={styles.entityDetailClose} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {invokingLabel ? <span className={styles.entityDetailInvoke}>{invokingLabel}</span> : null}
        <h2 className={styles.entityDetailTitle}>{title}</h2>
        {subtitle ? <p className={styles.entityDetailSubtitle}>{subtitle}</p> : null}
        {stateLabel ? <span className={styles.mcStatusPill}>{stateLabel}</span> : null}
      </header>

      <div className={styles.entityDetailBody}>
        {children}
        {core.length > 0 ? (
          <section className={styles.entityDetailSection} aria-label="Core properties">
            <h3 className={styles.entityDetailSectionTitle}>Properties</h3>
            <FieldRows fields={core} />
          </section>
        ) : null}
        {context.length > 0 ? (
          <details className={styles.entityDetailSection} open>
            <summary className={styles.entityDetailSectionTitle}>Context</summary>
            <FieldRows fields={context} />
          </details>
        ) : null}
        {mechanical.length > 0 ? (
          <details className={styles.entityDetailSection}>
            <summary className={styles.entityDetailSectionTitle}>
              Advanced ({mechanical.length})
            </summary>
            <FieldRows fields={mechanical} />
          </details>
        ) : null}
      </div>

      {footerPrimary ? <footer className={styles.entityDetailFooter}>{footerPrimary}</footer> : null}
    </aside>
  );
};
